import { useState } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import { Button } from '../../ui';
import './RecoveryCodesDisplay.css';

interface RecoveryCodesDisplayProps {
  codes: string[];
  // The first time we show the codes (after enrollment / regeneration) the
  // user MUST acknowledge they've saved them — we won't show them again.
  warning?: string;
  onAcknowledge?: () => void;
}

export function RecoveryCodesDisplay({ codes, warning, onAcknowledge }: RecoveryCodesDisplayProps) {
  const [copied, setCopied] = useState(false);

  const allCodes = codes.join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(allCodes);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (e.g. permissions); the download button
      // is the fallback.
    }
  };

  const handleDownload = () => {
    const blob = new Blob([allCodes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'authnz-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="recovery-codes-display" data-testid="recovery-codes-display">
      {warning && <p className="recovery-codes-display__warning">{warning}</p>}
      <ul className="recovery-codes-display__list" aria-label="Recovery codes">
        {codes.map(code => (
          <li key={code} className="recovery-codes-display__item">
            <code>{code}</code>
          </li>
        ))}
      </ul>

      <div className="recovery-codes-display__actions">
        <Button type="button" variant="secondary" onClick={handleCopy}>
          {copied ? (
            <>
              <Check size={16} aria-hidden="true" /> Copied
            </>
          ) : (
            <>
              <Copy size={16} aria-hidden="true" /> Copy codes
            </>
          )}
        </Button>
        <Button type="button" variant="secondary" onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> Download
        </Button>
        {onAcknowledge && (
          <Button type="button" onClick={onAcknowledge}>
            I&apos;ve saved my codes
          </Button>
        )}
      </div>
    </div>
  );
}
